"use client";

import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";

interface OtherReasonFeedbackStepProps {
  reason: string;
  noLongerNeedReason?: string;
  onBack: () => void;
  onFeedbackSubmitted: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const OtherReasonFeedbackStep = ({
  reason,
  noLongerNeedReason,
  onBack,
  onFeedbackSubmitted,
  onSubmit,
  isSubmitting,
}: OtherReasonFeedbackStepProps) => {
  const [body, setBody] = useState("");
  const t = useI18n();
  const trpc = useTRPC();

  const submitFeedback = useMutation(
    trpc.cancellation.submitCancellationFeedback.mutationOptions({
      onSuccess: () => {
        onFeedbackSubmitted();
        onSubmit();
      },
      onError: (error) => {
        toast.error(
          error.message || t("feedbackModal.feedbackForm.submitError"),
        );
      },
    }),
  );

  const handleSubmit = () => {
    if (!body.trim()) return;
    submitFeedback.mutate({
      reason,
      outcome: "canceled",
      noLongerNeedReason,
      feedbackText: body.trim(),
    });
  };

  const isPending = submitFeedback.isPending || isSubmitting;
  const canSubmit = body.trim().length > 0 && !isPending;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        {t("cancellation.otherReasonFeedback.description")}
      </p>

      <Textarea
        placeholder={t("cancellation.otherReasonFeedback.placeholder")}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="resize-vertical focus-visible:ring-brand-primary"
        rows={5}
      />

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1">
          <ChevronLeft className="size-4" />
          {t("cancellation.reason.goBack")}
        </Button>
        <Button
          variant="dangerOutline"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("cancellation.offers.continueCanceling")}
        </Button>
      </div>
    </div>
  );
};
